import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useConfigFormState } from "../../../../common/ConfigForm/useConfigFormState";
import { usePermissions } from "../../../../../hooks/usePermissions";
import { useLoading } from "../../../../../hooks/useLoading";
import { LOADING_KEYS } from "../../../../../constants/loadingKeys";
import { SUCCESS_STATUS, ERROR_STATUS } from "../../../../../constants";
import { customerSaveAsync, listOptionSalesCustomer, listOptionCustomerGroup } from "../../../../../app/customerSlice";
import { scrollToTop } from "../../../../../utils";
import { hydrateBasicInfoForm, buildBasicInfoPayload } from "../basicInfo.payload";
import { validateDomains, hasDomainErrors } from "../basicInfo.rules";

const hydrateHeader = (customerData) => ({
  customerStatus: customerData?.settings?.customerStatus === 1 ? 1 : 0,
  sessionTimeout: customerData?.settings?.sessionTimeout ?? 24,
  sessionTimeoutType: customerData?.settings?.sessionTimeoutType || "hours",
  companyLogo: customerData?.settings?.companyLogo ?? null,
});

export const useBasicInfo = ({ customerData }) => {
  const dispatch = useDispatch();
  const { register, handleSubmit, errors, reset, clearErrors } = useForm();
  const { hasPermission } = usePermissions();

  const accessError = useSelector((state) => state.app.accessError);
  const salesOfficeOptions = useSelector((state) => state.customer.listOption);
  const customerGroupOptions = useSelector((state) => state.customer.customerGroupType);

  const { form, setForm, setField } = useConfigFormState(() => hydrateBasicInfoForm(customerData));

  const [editable, setEditable] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [header, setHeader] = useState(() => hydrateHeader(customerData));
  const [previewImg, setPreviewImg] = useState(customerData?.settings?.companyLogo ?? null);
  const [buttonState, setButtonState] = useState(!customerData?.settings?.companyLogo);
  const [domainErrors, setDomainErrors] = useState([]);

  const saveLoading = useLoading(LOADING_KEYS.UPDATE_CUSTOMER);

  useEffect(() => {
    dispatch(listOptionSalesCustomer("salesoffice"));
    dispatch(listOptionCustomerGroup("customergroup"));
  }, [dispatch]);

  useEffect(() => {
    if (!saveStatus) return;
    const timer = setTimeout(() => setSaveStatus(null), 5000);
    return () => clearTimeout(timer);
  }, [saveStatus]);

  useEffect(() => {
    if (!editable) {
      setHeader(hydrateHeader(customerData));
    }
  }, [customerData, editable]);

  const extra = useMemo(() => ({ salesOfficeOptions, customerGroupOptions }), [salesOfficeOptions, customerGroupOptions]);

  const handleEdit = useCallback(() => {
    reset();
    setForm(hydrateBasicInfoForm(customerData));
    setHeader(hydrateHeader(customerData));
    setPreviewImg(customerData?.settings?.companyLogo ?? null);
    setButtonState(!customerData?.settings?.companyLogo);
    setDomainErrors([]);
    setEditable((prev) => !prev);
  }, [customerData, reset, setForm]);

  const onFieldChange = useCallback(
    (key, value, options = {}) => {
      setField(key, value);

      if (key === "country") {
        setField("state", "");
        clearErrors(["state"]);
      }

      if (key === "domain" && !options.skipValidate) {
        setDomainErrors(validateDomains(value));
      }
    },
    [setField, clearErrors]
  );

  const onToggleStatus = useCallback(() => {
    setHeader((prev) => ({ ...prev, customerStatus: prev.customerStatus === 1 ? 0 : 1 }));
  }, []);

  const onSessionTimeoutChange = useCallback((value) => {
    setHeader((prev) => ({ ...prev, sessionTimeout: value }));
  }, []);

  const onSessionTimeoutTypeChange = useCallback(
    (value) => {
      setHeader((prev) => ({ ...prev, sessionTimeoutType: value }));
      clearErrors(["sessionTimeOut"]);
    },
    [clearErrors]
  );

  useEffect(() => {
    setHeader((prev) => ({ ...prev, companyLogo: previewImg }));
  }, [previewImg]);

  const doSubmit = useCallback(async () => {
    const nextDomainErrors = validateDomains(form.domain);
    if (hasDomainErrors(nextDomainErrors)) {
      setDomainErrors(nextDomainErrors);
      return;
    }

    const payload = buildBasicInfoPayload({ form, customerData, header });
    try {
      await dispatch(customerSaveAsync(payload));
      setEditable(false);
      reset();
      setSaveStatus(SUCCESS_STATUS);
      scrollToTop();
    } catch (error) {
      setSaveStatus(ERROR_STATUS);
    }
  }, [form, customerData, header, dispatch, reset]);

  return {
    register,
    handleSubmit,
    errors,
    form,
    extra,
    hasPermission,
    onFieldChange,
    submitErrors: { domainErrors },

    editable,
    handleEdit,
    saveLoading,
    accessError,
    saveStatus,

    header,
    onToggleStatus,
    onSessionTimeoutChange,
    onSessionTimeoutTypeChange,
    previewImg,
    setPreviewImg,
    buttonState,
    setButtonState,

    doSubmit,
  };
};
